/* RGM redesign product page: gallery and Buy Now sync. No dependencies. */
(function () {
  'use strict';

  function hasPubSub() {
    return typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined';
  }

  function prefersReducedMotion() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  if (!customElements.get('rgm2-pdp-gallery')) {
    customElements.define(
      'rgm2-pdp-gallery',
      class Rgm2PdpGallery extends HTMLElement {
        connectedCallback() {
          if (!this.initialized) this.init();
          if (this.count > 1 && hasPubSub() && !this.unsubscribe) {
            this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, (event) => this.onVariantChange(event));
          }
        }

        disconnectedCallback() {
          if (this.unsubscribe) {
            this.unsubscribe();
            this.unsubscribe = null;
          }
        }

        init() {
          this.initialized = true;
          this.track = this.querySelector('[data-rgm2-track]');
          this.slides = Array.prototype.slice.call(this.querySelectorAll('[data-rgm2-slide]'));
          this.thumbs = Array.prototype.slice.call(this.querySelectorAll('[data-rgm2-thumb]'));
          this.current = this.querySelector('[data-rgm2-current]');
          this.live = this.querySelector('[data-rgm2-live]');
          this.count = this.slides.length;
          this.index = 0;
          this.pending = null;
          if (!this.track || this.count < 2) return;

          const moreThumb = this.querySelector('[data-rgm2-more]');
          this.moreIndex = moreThumb ? Number(moreThumb.dataset.rgm2Thumb) : null;

          const prev = this.querySelector('[data-rgm2-prev]');
          const next = this.querySelector('[data-rgm2-next]');
          if (prev) prev.addEventListener('click', () => this.go(this.index - 1, true));
          if (next) next.addEventListener('click', () => this.go(this.index + 1, true));
          this.thumbs.forEach((thumb) => thumb.addEventListener('click', () => this.onThumb(thumb)));
          this.addEventListener('keydown', (event) => this.onKeydown(event));
          this.track.addEventListener(
            'scroll',
            () => {
              this.hydrate();
              this.onScroll();
            },
            { passive: true }
          );
          this.track.addEventListener('scrollend', () => this.onScrollEnd());
          // Start loading the deferred slides, and the ones next to the current slide, as soon as a
          // swipe begins (touch or pen) or a mouse or keyboard user reaches the gallery.
          const warm = () => this.warm();
          this.track.addEventListener('touchstart', warm, { passive: true });
          this.addEventListener('pointerdown', warm, { passive: true });
          this.addEventListener('pointerenter', warm, { passive: true });
          this.addEventListener('focusin', warm);

          this.updateSlides(0);
          this.scheduleHydrate();
        }

        // Slides 2 and later are rendered with data-src/data-srcset/data-sizes (see rgm2-main-product),
        // so they never compete with the first image. They are filled in on the first interaction or
        // when the browser is idle after load.
        scheduleHydrate() {
          if (!this.querySelector('img[data-src]')) {
            this.hydrated = true;
            return;
          }
          const whenIdle = () => {
            if ('requestIdleCallback' in window) {
              window.requestIdleCallback(() => this.hydrate(), { timeout: 4000 });
            } else {
              setTimeout(() => this.hydrate(), 1500);
            }
          };
          if (document.readyState === 'complete') {
            whenIdle();
          } else {
            window.addEventListener('load', whenIdle, { once: true });
          }
        }

        hydrate() {
          if (this.hydrated) return;
          this.hydrated = true;
          this.querySelectorAll('img[data-src]').forEach((img) => {
            if (img.dataset.sizes) img.setAttribute('sizes', img.dataset.sizes);
            if (img.dataset.srcset) img.setAttribute('srcset', img.dataset.srcset);
            img.setAttribute('src', img.dataset.src);
            img.removeAttribute('data-sizes');
            img.removeAttribute('data-srcset');
            img.removeAttribute('data-src');
          });
          // The slides next to the current one (slide 2 on a fresh page) load right away, so a
          // swipe shows them without waiting for lazy loading inside the scroller.
          this.prime(this.index);
        }

        warm() {
          this.hydrate();
          this.prime(this.index);
        }

        // Load the given slide and its neighbours right away instead of waiting for lazy loading.
        // Their low fetch priority only mattered while the first image was loading.
        prime(index) {
          [index - 1, index, index + 1].forEach((i) => {
            const slide = this.slides[i];
            const img = slide && slide.querySelector('img');
            if (img && img.hasAttribute('src') && img.getAttribute('loading') === 'lazy') {
              img.setAttribute('fetchpriority', 'auto');
              img.setAttribute('loading', 'eager');
            }
          });
        }

        onScrollEnd() {
          this.pending = null;
          clearTimeout(this.pendingTimer);
          this.syncFromScroll();
        }

        onThumb(thumb) {
          const target = Number(thumb.dataset.rgm2Thumb);
          if (thumb.hasAttribute('data-rgm2-more') && this.index >= target) {
            // The "+N" thumb keeps stepping through the remaining images.
            this.go(this.index + 1 < this.count ? this.index + 1 : target, true);
            return;
          }
          this.go(target, true);
        }

        onKeydown(event) {
          if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
          if (event.altKey || event.ctrlKey || event.metaKey) return;
          event.preventDefault();
          this.go(this.index + (event.key === 'ArrowRight' ? 1 : -1), true);
        }

        go(index, announce) {
          const target = ((index % this.count) + this.count) % this.count;
          this.hydrate();
          this.prime(target);
          this.pending = target;
          clearTimeout(this.pendingTimer);
          // Fallback for browsers without the scrollend event.
          this.pendingTimer = setTimeout(() => this.onScrollEnd(), 1500);
          this.track.scrollTo({
            left: target * this.track.clientWidth,
            behavior: prefersReducedMotion() ? 'auto' : 'smooth',
          });
          this.setActive(target, announce);
        }

        onScroll() {
          if (this.frame) return;
          this.frame = requestAnimationFrame(() => {
            this.frame = null;
            if (this.pending !== null) {
              if (this.scrollIndex() === this.pending) this.pending = null;
              return;
            }
            this.syncFromScroll();
          });
        }

        scrollIndex() {
          const width = this.track.clientWidth || 1;
          return Math.min(this.count - 1, Math.max(0, Math.round(this.track.scrollLeft / width)));
        }

        syncFromScroll() {
          const index = this.scrollIndex();
          if (index !== this.index) this.setActive(index, false);
        }

        setActive(index, announce) {
          this.index = index;
          this.prime(index);
          if (this.current) this.current.textContent = String(index + 1);
          const activeThumb = this.moreIndex !== null && index >= this.moreIndex ? this.moreIndex : index;
          this.thumbs.forEach((thumb) => {
            thumb.setAttribute('aria-current', Number(thumb.dataset.rgm2Thumb) === activeThumb ? 'true' : 'false');
          });
          this.updateSlides(index);
          if (announce && this.live) {
            const slide = this.slides[index];
            this.live.textContent = (slide && slide.getAttribute('aria-label')) || String(index + 1) + ' / ' + this.count;
          }
        }

        // Only the visible slide stays in the reading order. Slides hold no focusable content,
        // so aria-hidden is enough and hit testing (and scroll-snap swiping) is left untouched.
        updateSlides(index) {
          this.slides.forEach((slide, i) => {
            if (i === index) {
              slide.removeAttribute('aria-hidden');
            } else {
              slide.setAttribute('aria-hidden', 'true');
            }
          });
        }

        onVariantChange(event) {
          const data = event && event.data;
          if (!data || data.sectionId !== this.dataset.section) return;
          const media = data.variant && data.variant.featured_media;
          if (!media) return;
          const target = this.slides.findIndex((slide) => slide.dataset.mediaId === String(media.id));
          if (target > -1 && target !== this.index) this.go(target);
        }
      }
    );
  }

  if (!customElements.get('rgm2-pdp-buy')) {
    customElements.define(
      'rgm2-pdp-buy',
      class Rgm2PdpBuy extends HTMLElement {
        connectedCallback() {
          this.buyForm = this.querySelector('[data-rgm2-buy-now]');
          this.addInput = this.querySelector('form[data-type="add-to-cart-form"] input[name="id"]');
          if (!this.buyForm || !this.addInput) return;
          this.buyInput = this.buyForm.querySelector('input[name="id"]');

          if (!this.onInputChange) {
            // Dawn product-info updates the Add to Cart variant id; mirror it for Buy Now.
            this.onInputChange = () => {
              this.buyInput.value = this.addInput.value;
              if (!this.addInput.value) this.buyForm.hidden = true;
            };
            this.addInput.addEventListener('change', this.onInputChange);
          }

          if (hasPubSub() && !this.unsubscribe) {
            this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, (event) => this.onVariantChange(event));
          }
        }

        disconnectedCallback() {
          if (this.unsubscribe) {
            this.unsubscribe();
            this.unsubscribe = null;
          }
        }

        onVariantChange(event) {
          const data = event && event.data;
          if (!data || data.sectionId !== this.dataset.section) return;
          const variant = data.variant;
          this.buyInput.value = variant ? variant.id : '';
          this.buyForm.hidden = !(variant && variant.available);

          // Dawn swaps in its own button strings; restore the merchant's label from the fresh render.
          const id = 'ProductSubmitButton-' + this.dataset.section;
          const fresh = data.html && data.html.getElementById(id);
          const freshLabel = fresh && fresh.querySelector('[data-rgm2-label]');
          const button = document.getElementById(id);
          const label = button && button.querySelector('[data-rgm2-label]');
          if (freshLabel && label) label.textContent = freshLabel.textContent;
        }
      }
    );
  }
})();
